import { isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import { useSetRecoilState } from 'recoil';

import { Button } from '@/components/button';
import { Image } from '@/components/image';
import { NotificationItem } from '@/components/modules/user-notifications/item';
import { Text } from '@/components/text';
import { LoadingSpinner } from '@/components/ui/loading/spinner';
import { UserMessage } from '@/components/ui/user-message';
import { FullUserNotificationModel } from '@/lib/models/notification/types';
import { appNavState } from '@/state/navigation';
import { getUserNotificationAction } from '@/utils/helpers/user-notifications';
import { tv } from '@/utils/styles';

export interface UserNotificationsListProps {
  notifications: FullUserNotificationModel[];
  isLoading?: boolean;
  error?: string;
  onLoadMore?: () => void;
  shouldShowLoadMore?: boolean;
  onMarkAsRead: (id: string, isRead: boolean) => void;
}

const UserNotificationsList = ({
  notifications,
  isLoading,
  error,
  onLoadMore,
  shouldShowLoadMore,
  onMarkAsRead,
}: UserNotificationsListProps) => {
  const router = useRouter();
  const setAppNav = useSetRecoilState(appNavState);
  const { base, list } = styles();

  const handleClick = (notification: FullUserNotificationModel) => {
    const { href } = getUserNotificationAction(notification) || {};
    if (href) {
      onMarkAsRead(notification.id || '', true);
      setAppNav((prev) => ({
        ...prev,
        isNotificationsOpen: false,
      }));
      router.push(href);
    }
  };

  if (error) return <UserMessage variant="error" body={error} className="!mt-0" />;

  if (isLoading) return <LoadingSpinner isVisible />;

  if (isEmpty(notifications))
    return (
      <div className="flex flex-col items-center gap-4 pt-4">
        <Image className="size-40" src="/assets/no-notifications.png" alt="No notifications" />
        <Text>No notifications</Text>
      </div>
    );

  return (
    <div className={base()}>
      <ol className={list()}>
        {notifications.map((notification, i) => {
          return (
            <li key={i}>
              <NotificationItem
                fullNotification={notification}
                onMarkAsRead={onMarkAsRead}
                onClick={() => handleClick(notification)}
              />
            </li>
          );
        })}
      </ol>
      {shouldShowLoadMore ? (
        <Button variant="link" onClick={onLoadMore} className="text-sm">
          Load more...
        </Button>
      ) : null}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'flex flex-col gap-4',
    list: 'flex flex-col gap-2',
  },
});

export { UserNotificationsList };
